import { isGameLocale, type Locale } from './locales';

/**
 * Player-chosen UI language, shared by Reddit inline and expanded views on the same origin.
 * Read synchronously at boot (before the platform gateway exists), so it lives in localStorage
 * (sessionStorage when blocked) like the sound and haptics preferences. 'auto' (or no value) keeps following the device.
 */
export type LocalePreference = 'auto' | Locale;
export const localePreferenceKey = 'nem:locale:v1';
let memory: LocalePreference | undefined;

const stores = (): Storage[] => {
  const found: Storage[] = [];
  for (const read of [() => localStorage, () => sessionStorage]) {
    try {
      const store = read();
      if (store) {
        found.push(store);
      }
    } catch { /* Unavailable store. */ }
  }
  return found;
};

const readStored = (): LocalePreference | undefined => {
  for (const store of stores()) {
    try {
      const stored = store.getItem(localePreferenceKey);
      if (stored === 'auto' || isGameLocale(stored)) {
        return stored;
      }
    } catch { /* Try the next store. */ }
  }
  return undefined;
};

export function readLocalePreference(): LocalePreference {
  return readStored() ?? memory ?? 'auto';
}

/**
 * Saves to localStorage, else sessionStorage (survives the reload that applies it). Returns false
 * unless the next read returns the new value, so callers must not reload into the old language.
 */
export function writeLocalePreference(value: LocalePreference): boolean {
  const rejected: Storage[] = [];
  for (const store of stores()) {
    try {
      if (value === 'auto') {
        store.removeItem(localePreferenceKey);
        continue;
      }
      store.setItem(localePreferenceKey, value);
      // A readable store that rejected the write must not keep a stale choice ahead of this one.
      for (const stale of rejected) {
        try {
          stale.removeItem(localePreferenceKey);
        } catch { /* Checked by the read-back below. */ }
      }
      break;
    } catch {
      rejected.push(store);
    }
  }
  const persisted = (readStored() ?? 'auto') === value;
  if (persisted) {
    // An unsaved choice is never applied, so it must not become this run's language either.
    memory = value;
  }
  return persisted;
}

/** The saved explicit language, or undefined to follow the host/device. */
export function savedLocale(): Locale | undefined {
  const value = readLocalePreference();
  return value === 'auto' ? undefined : value;
}
